import React, { useEffect } from 'react';
import {
  $addManagerIncomingWebhookBody,
  $updateManagerIncomingWebhookBody,
  AddManagerIncomingWebhookBody,
  ManagerIncomingWebhookAttributes,
  UpdateManagerIncomingWebhookBody,
} from '@loyalty/shared-common/manager-api-client';
import { Alert, Box, Button, FormControl, MenuItem, TextField, Tooltip } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Feature, extractErrorMessage } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IncomingWebhookAuthenticationType } from '@loyalty/shared-common/ingestor-api-client';
import { QlfFormRow } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { IncomingWebhookAuthenticationForm } from './IncomingWebhookAuthenticationForm';
import { IncomingWebhookMappedEventConfig } from './mapped-events/IncomingWebhookMappedEventConfig';
import { IncomingWebhookUrl } from './IncomingWebhookUrl';
import { Title } from '../../../shared/layout/Title';
import {
  formatInteractionLabel,
  isInteractionSelectable,
} from '../../../interactions/interaction-utils';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';
import { useIncomingWebhooks } from '../../IncomingWebhookContext';
import { useInteractions } from '../../../interactions/use-interactions';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';

const EMPTY_AUTHENTICATION_CONFIG = {
  type: IncomingWebhookAuthenticationType.HUB_SIGNATURE,
  secret: '',
  verificationSecret: '',
} as const;

export function IncomingWebhookForm({
  webhook,
}: {
  webhook: ManagerIncomingWebhookAttributes;
}): React.ReactElement {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { write: webhookWrite } = features[Feature.INCOMING_WEBHOOK];
  const { read: interactionEnabled } = features[Feature.INTERACTION];
  const { addWebhook, updateWebhook, isLoading } = useIncomingWebhooks();
  const { currentAccount } = useAccounts();
  const { integrationApi } = useApi();
  const interactions = useInteractions({ withDeleted: true });

  const integrations = useQuery({
    queryKey: ['integrations', currentAccount.id, 'incomingEvent'],
    queryFn: () =>
      integrationApi.getIntegrations(currentAccount.id, {
        params: { category: 'incomingEvent' },
      }),
  });
  const resolver = useJoiResolver<AddManagerIncomingWebhookBody | UpdateManagerIncomingWebhookBody>(
    webhook.isNew ? $addManagerIncomingWebhookBody : $updateManagerIncomingWebhookBody,
  ) as any;
  const form = useForm<AddManagerIncomingWebhookBody | UpdateManagerIncomingWebhookBody>({
    mode: 'onChange',
    resolver,
    shouldUnregister: false,
    defaultValues: webhook,
  });
  const {
    control,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { isDirty, isSubmitting, errors },
  } = form;

  useEffect(() => {
    reset(webhook as AddManagerIncomingWebhookBody | UpdateManagerIncomingWebhookBody);
  }, [reset, webhook]);

  const isNew = webhook['isNew'];
  const integrationManagedAuthentication = !!watch('config.integrationId');

  useLeaverPrompt(isDirty || isSubmitting);

  const onSubmit = async (
    webhook: AddManagerIncomingWebhookBody | UpdateManagerIncomingWebhookBody,
  ) => {
    // The integration owns the authentication, so nothing is stored on the webhook itself.
    const payload = webhook.config?.integrationId
      ? { ...webhook, authenticationConfig: null }
      : webhook;

    if (payload['isNew']) {
      addWebhook(payload as AddManagerIncomingWebhookBody);
    } else {
      updateWebhook(payload as UpdateManagerIncomingWebhookBody);
    }
  };

  const loading = isLoading('addWebhook') || isLoading('updateWebhook');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <DetailForm>
          <DetailForm.Form>
            <Title>{t('incoming-webhooks.form.title')}</Title>
            <Box mb={2}>{!isNew && <IncomingWebhookUrl url={webhook.url} />}</Box>
            <Controller
              control={control}
              name="name"
              defaultValue=""
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                return (
                  <QlfFormRow label={t('incoming-webhooks.inputs.name.label')}>
                    <TextField
                      {...field}
                      disabled={!webhookWrite}
                      inputRef={ref}
                      placeholder={t('incoming-webhooks.inputs.name.placeholder')}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </QlfFormRow>
                );
              }}
            />
            <Controller
              control={control}
              name="config.integrationId"
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                return (
                  <FormControl error={!!error}>
                    <QlfFormRow label={t('incoming-webhooks.inputs.integration')}>
                      <TextField
                        select
                        SelectProps={{ displayEmpty: true }}
                        inputRef={ref}
                        {...field}
                        value={field.value ?? ''}
                        onChange={event => {
                          field.onChange(event.target.value);
                          setValue(
                            'authenticationConfig',
                            event.target.value ? null : EMPTY_AUTHENTICATION_CONFIG,
                            { shouldDirty: true },
                          );
                        }}
                        disabled={!webhookWrite || integrations.isPending}
                        helperText={extractErrorMessage(error)}
                      >
                        <MenuItem value="">
                          {t('incoming-webhooks.inputs.integration.none')}
                        </MenuItem>
                        {(integrations.data ?? []).map(integration => (
                          <MenuItem value={String(integration.id)} key={integration.id}>
                            {integration.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </QlfFormRow>
                  </FormControl>
                );
              }}
            />
            {interactionEnabled ? (
              <Controller
                control={control}
                name="config.interactionId"
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <FormControl error={!!error}>
                    <QlfFormRow label={t('incoming-webhooks.inputs.interaction')}>
                      <TextField
                        select
                        SelectProps={{ displayEmpty: true }}
                        inputRef={ref}
                        {...field}
                        value={field.value ?? ''}
                        onChange={event => field.onChange(event.target.value)}
                        disabled={!webhookWrite || interactions.isPending}
                        helperText={extractErrorMessage(error)}
                      >
                        <MenuItem value="">
                          {t('incoming-webhooks.inputs.interaction.none')}
                        </MenuItem>
                        {(interactions.data ?? [])
                          .filter(interaction => isInteractionSelectable(interaction, field.value))
                          .map(interaction => (
                            <MenuItem
                              value={interaction.id}
                              key={interaction.id}
                              disabled={!!interaction.deletedAt}
                            >
                              {formatInteractionLabel(interaction, t)}
                            </MenuItem>
                          ))}
                      </TextField>
                    </QlfFormRow>
                  </FormControl>
                )}
              />
            ) : null}
            {integrationManagedAuthentication ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('incoming-webhooks.inputs.integration.authentication-managed')}
              </Alert>
            ) : (
              <IncomingWebhookAuthenticationForm />
            )}
            <IncomingWebhookMappedEventConfig />
          </DetailForm.Form>
          <DetailForm.Footer>
            <Tooltip
              title={webhookWrite ? '' : t('shared.action.permission.required')}
              placement="left"
            >
              <div>
                <Button
                  startIcon={
                    <FontAwesomeIcon
                      fontSize="inherit"
                      icon={loading ? faSpinner : faCheck}
                      spin={loading}
                    />
                  }
                  color="success"
                  type="submit"
                  disabled={!isDirty || loading || !webhookWrite}
                >
                  {t('incoming-webhooks.save.button')}
                </Button>
              </div>
            </Tooltip>
          </DetailForm.Footer>
        </DetailForm>
      </form>
    </FormProvider>
  );
}
